import React from 'react';
import { MapPin, Shield, ArrowLeft } from 'lucide-react';
import ProductGrid from './ProductGrid';

interface Product {
  id: number;
  name: string;
  price: number;
  image: string;
  description: string;
}

interface StoreDetailProps {
  store: {
    id: string | number;
    store_name: string;
    craft_professions?: {
      name: string;
      category: string;
    };
    rating: number;
    reviews: number;
    location: string;
    avatar_url: string;
    verified: boolean;
    description: string;
    owner_name: string;
    products?: Product[];
    website?: string;
  };
  onBack?: () => void;
}

export default function StoreDetail({ store, onBack }: StoreDetailProps) {
  const [activeTab, setActiveTab] = React.useState('products');
  const [imageError, setImageError] = React.useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const imageUrl = imageError 
    ? `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(store.store_name)}`
    : store.avatar_url;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button 
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to marketplace
        </button>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column - Details */}
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <h1 className="text-2xl font-bold text-gray-900">{store.store_name}</h1>
                  {store.verified && (
                    <Shield className="w-6 h-6 text-blue-600" />
                  )}
                </div>

                <p className="text-lg text-blue-600">
                  {store.craft_professions?.category || 'Artisan'}
                </p>

                <div className="flex items-center text-gray-600">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>{store.location}</span>
                </div>

                <div className="pt-4">
                  <h2 className="font-semibold text-gray-900 mb-2">About Us</h2>
                  <p className="text-gray-600">{store.description}</p>
                </div>

                {store.owner_name && (
                  <div className="pt-2">
                    <h2 className="font-semibold text-gray-900 mb-2">Owner</h2>
                    <p className="text-gray-600">{store.owner_name}</p>
                  </div>
                )}

                {store.website && (
                  <div className="pt-2">
                    <a
                      href={store.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-blue-600 hover:text-blue-700"
                    >
                      Visit Our Website
                      <ArrowLeft className="w-4 h-4 ml-1 transform rotate-180" />
                    </a>
                  </div>
                )}
              </div>

              {/* Right Column - Image */}
              <div>
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={imageUrl}
                    alt={store.store_name}
                    className="w-full h-full object-cover"
                    onError={handleImageError}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Products</h2>
          </div>

          {store.products && store.products.length > 0 ? (
            <ProductGrid products={store.products} />
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm">
              <p className="text-gray-500 text-lg">No products available yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}